import type { Priority } from "@app/core";
import { pastel } from "../../task-colors.js";

/** Muted pastel for the priority rail tabs + wash (from the design mockup). */
export const PRIORITY_PAPER: Array<{ id: Priority; name: string; color: string }> = [
  { id: "none", name: "No priority", color: "oklch(0.90 0.005 78)" },
  { id: "low", name: "Low", color: "oklch(0.87 0.055 150)" },
  { id: "medium", name: "Medium", color: "oklch(0.86 0.075 75)" },
  { id: "high", name: "High", color: "oklch(0.80 0.105 28)" },
];

/** Brighter, saturated palette used only for the subtask priority flags. */
export const SUB_PRI_FLAG: Record<Priority, string> = {
  none: "oklch(0.90 0.005 78)",
  low: "oklch(0.74 0.155 150)",
  medium: "oklch(0.80 0.170 80)",
  high: "oklch(0.64 0.220 28)",
};

/** Neutral wash fallback when no topic is chosen. */
export const NO_TOPIC_COLOR = "oklch(0.89 0.010 250)";

/** Soften a stored topic/custom hex into the design's muted-pastel register. */
export function paperTint(hex: string | null | undefined, amount = 0.55): string {
  return pastel(hex || "#b8b2a8", amount);
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "Wed, Jun 10 · 2:00 PM" (time omitted when all-day / no time picked). */
export function fmtDateTime(
  dateStr: string,
  hour: number | null,
  minute: number | null,
  hour12: boolean,
  locale?: string,
): string {
  if (!dateStr) return "—";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString(locale, { weekday: "short" });
  const date = d.toLocaleDateString(locale, { month: "short", day: "numeric" });
  if (hour === null || minute === null) return `${day}, ${date}`;
  const t = new Date(2000, 0, 1, hour, minute);
  const time = t.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", hour12 });
  return `${day}, ${date} · ${time}`;
}

/** "2:00 PM" / "14:00" for the chip's clickable time part. */
export function fmtClock(hour: number, minute: number, hour12: boolean, locale?: string): string {
  const t = new Date(2000, 0, 1, hour, minute);
  return t.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit", hour12 });
}

/** "Jun 10, 2026" for date-only chips. */
export function fmtDate(dateStr: string, locale?: string): string {
  if (!dateStr) return "—";
  const d = new Date(`${dateStr}T00:00`);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
}

/** Today + `days` as an <input type="date"> value. */
export function isoDateOffset(days = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** First grapheme of a topic name — the sticker monogram. */
export function stickerGlyph(name: string): string {
  return [...name][0] ?? "?";
}
